/**
 * Company catalog + financials — the M4 analysis data.
 *
 * Verbatim from the original prototype `COMPANIES{}` (app.js L309-389) — "Sync II"
 * financials refresh (2026-06-08), each at its latest reported FY. Plus the
 * NovaTech practice company (`NOVATECH` app.js L285-298) used in M3.
 *
 * Invariants that hold exactly for every statement (3 years each):
 *   revenue − cogs       = grossProfit
 *   grossProfit − opex   = opIncome
 *
 * Render gotchas carried faithfully (see EXPERIENCE-MODEL.md §4–§5):
 *   • currency is per-company: Spotify is `€` (IFRS, EUR); all others `$`.
 *   • magnitude is per-company: NovaTech + Duolingo are `M` (millions); rest `B`.
 *   • Nvidia uses an offset fiscal window (FY2024–FY2026).
 *   • negative op income is real for Spotify FY2023 and Duolingo FY2023.
 */

export type Currency = '$' | '€';
export type Magnitude = 'B' | 'M';

export type CompanyId = 'spotify' | 'airbnb' | 'shopify' | 'nvidia' | 'duolingo';

/** One income-statement line across all reported years (positional, aligned to `years`). */
export type StatementRow = readonly number[];

export interface IncomeStatement {
  cogs: StatementRow;
  grossProfit: StatementRow;
  opex: StatementRow;
  opIncome: StatementRow;
  revenue: StatementRow;
}

export interface Company {
  accent: string;
  currency: Currency;
  id: CompanyId;
  /** Path under /public — real brand SVG for all 5. */
  logo: string;
  magnitude: Magnitude;
  monogram: string;
  name: string;
  rows: IncomeStatement;
  tagline: string;
  tags: readonly string[];
  ticker: string;
  years: readonly string[];
}

export const COMPANIES: Record<CompanyId, Company> = {
  spotify: {
    id: 'spotify',
    name: 'Spotify',
    ticker: 'SPOT',
    monogram: 'S',
    accent: '#1DB954',
    logo: '/images/rec/tickers/spotify.svg',
    tagline: 'Subscription streaming · loss → profit turnaround',
    tags: ['Subscription', 'B2C', 'Turnaround'],
    currency: '€',
    magnitude: 'B',
    years: ['FY2023', 'FY2024', 'FY2025'],
    rows: {
      revenue: [13.2, 15.7, 17.2],
      cogs: [9.8, 11.0, 11.7],
      grossProfit: [3.4, 4.7, 5.5],
      opex: [3.8, 3.3, 3.3],
      opIncome: [-0.4, 1.4, 2.2],
    },
  },
  airbnb: {
    id: 'airbnb',
    name: 'Airbnb',
    ticker: 'ABNB',
    monogram: 'A',
    accent: '#FF5A5F',
    logo: '/images/rec/tickers/airbnb.svg',
    tagline: 'Marketplace · post-pandemic recovery · high margins',
    tags: ['Marketplace', 'B2C', 'High Margin'],
    currency: '$',
    magnitude: 'B',
    years: ['FY2023', 'FY2024', 'FY2025'],
    rows: {
      revenue: [9.9, 11.1, 12.2],
      cogs: [1.7, 1.9, 2.0],
      grossProfit: [8.2, 9.2, 10.2],
      opex: [5.4, 5.6, 6.3],
      opIncome: [2.8, 3.6, 3.9],
    },
  },
  shopify: {
    id: 'shopify',
    name: 'Shopify',
    ticker: 'SHOP',
    monogram: 'S',
    accent: '#7AB55C',
    logo: '/images/rec/tickers/shopify.svg',
    tagline: 'E-commerce platform · restructuring → profitability',
    tags: ['SaaS', 'B2B', 'Restructuring'],
    currency: '$',
    magnitude: 'B',
    years: ['FY2023', 'FY2024', 'FY2025'],
    rows: {
      revenue: [7.1, 8.9, 11.6],
      cogs: [2.9, 3.6, 4.6],
      grossProfit: [4.2, 5.3, 7.0],
      opex: [3.1, 3.4, 4.4],
      opIncome: [1.1, 1.9, 2.6],
    },
  },
  nvidia: {
    id: 'nvidia',
    name: 'Nvidia',
    ticker: 'NVDA',
    monogram: 'N',
    accent: '#76B900',
    logo: '/images/rec/tickers/nvidia.svg',
    tagline: 'Semiconductors · AI-driven hypergrowth',
    tags: ['Semis', 'B2B', 'AI'],
    currency: '$',
    magnitude: 'B',
    // Offset fiscal window vs the others (fiscal-2026 ended ~Jan 2026).
    years: ['FY2024', 'FY2025', 'FY2026'],
    rows: {
      revenue: [61, 130, 216],
      cogs: [17, 33, 63],
      grossProfit: [44, 97, 153],
      opex: [11, 15, 23],
      opIncome: [33, 82, 130],
    },
  },
  duolingo: {
    id: 'duolingo',
    name: 'Duolingo',
    ticker: 'DUOL',
    monogram: 'D',
    accent: '#58CC02',
    logo: '/images/rec/tickers/duolingo.svg',
    tagline: 'Edtech · fast growth · first profitable year',
    tags: ['Edtech', 'B2C', 'First Profit'],
    currency: '$',
    magnitude: 'M',
    years: ['FY2023', 'FY2024', 'FY2025'],
    rows: {
      revenue: [531, 748, 1038],
      cogs: [142, 204, 289],
      grossProfit: [389, 544, 749],
      opex: [402, 481, 613],
      opIncome: [-13, 63, 136],
    },
  },
};

/** Ordered company ids for the M4 catalog picker. */
export const COMPANY_IDS: readonly CompanyId[] = ['spotify', 'airbnb', 'shopify', 'nvidia', 'duolingo'] as const;

/**
 * Format a statement value with the company's per-company currency + magnitude — the render
 * gotcha the M4 activity template MUST carry (EXPERIENCE-MODEL.md §5a): Spotify is `€` (IFRS,
 * EUR); all others `$`. NovaTech + Duolingo are `M` (millions); the rest `B`. A negative value
 * keeps the sign in front of the currency (e.g. Spotify FY2023 op income → `-€0.4B`), and the
 * caller renders negatives red. The unit string (e.g. `($B)` / `(€B)`) for table headers is
 * exposed separately via `unitLabel`.
 */
export function formatMoney(company: Pick<Company, 'currency' | 'magnitude'>, value: number): string {
  const sign = value < 0 ? '-' : '';
  return `${sign}${company.currency}${Math.abs(value)}${company.magnitude}`;
}

/** The currency+magnitude unit shown in a statement-table column header, e.g. `($B)` or `(€B)`. */
export function unitLabel(company: Pick<Company, 'currency' | 'magnitude'>): string {
  return `(${company.currency}${company.magnitude})`;
}

// ---------------------------------------------------------------------------
// NovaTech — the M3 "Read the Numbers" practice company (static, not in catalog).
// ---------------------------------------------------------------------------

export interface PracticeCompany {
  chip: string;
  /** Expected practice-ratio answers, validated to ±1.0 tolerance. */
  expected: { revGrowth: number; grossMargin: number; opMargin: number };
  magnitude: Magnitude;
  name: string;
  rows: IncomeStatement;
  years: readonly string[];
}

export const NOVATECH: PracticeCompany = {
  name: 'NovaTech Inc.',
  chip: 'Practice example',
  years: ['FY2022', 'FY2023', 'FY2024'],
  magnitude: 'M',
  rows: {
    revenue: [40, 48, 55],
    cogs: [22, 25, 27],
    grossProfit: [18, 23, 28],
    opex: [12, 16, 21],
    opIncome: [6, 7, 7],
  },
  expected: { revGrowth: 37.5, grossMargin: 50.9, opMargin: 12.7 },
};
